import type {
  Vec2,
  Vec3,
  EntityId,
  TeamIdx,
  Tick,
  ActionKind,
  CardKind,
  MatchPhase,
  RestartKind,
  ModeInfo,
  PitchCondition,
} from '../types';

// Match state model (§4.3). Grows one interface per milestone slice; every field that survives a
// keyframe seek or feeds a rule must also be in the codec + hashState (§4.3, §9.4).

export interface PlayerState {
  id: EntityId;
  team: TeamIdx;
  pos: Vec2;
  vel: Vec2;
  facing: number;
  z: number;
  vz: number;
  stamina: number; // 0..1
  action: ActionKind;
  actionTicks: number;
  charge: { button: number; ticks: number } | null;
  sprinting: boolean; // sprint hysteresis latch (§5.2 T-ACT-013). In codec + hashState. See DEVIATIONS D5.
  prevButtons: number; // u32 (D45/D48) — last tick's buttons; REQUIRED for edge-triggering across keyframe seek (§4.3)
  shouting: boolean; // BTN.SHOUT held; feeds snapshot FLAGS + hearing graph
  calling: boolean; // D42: BTN.CALL held — the "pass to me" signal (bots obey a calling human; passes favor callers)
  offsideLiable: boolean; // §6.3; in codec + hashState
  /** D62: AUTHORITATIVE one-touch intent (the server-side D54). A kick press swallowed because the ball
   *  was not yet kickable ARMS these bits; only armed-and-still-held bits may fire the D53 fresh-claim
   *  volley. Without it the fresh-claim path read raw HELD buttons, so the very press that launched a
   *  pass fired again the instant the ball came back — the "instant re-pass" report. D54 masked this
   *  client-side only, and online the client learns it has the ball a full RTT late. In codec + hash. */
  intentBits: number; // u32 armed kick bits
  intentTick: number; // tick they were armed (expires after actions.kick.INTENT_TICKS)
  card: CardKind | null;
  sentOff: boolean;
  /** D65c: red-card SUSPENSION deadline (tick the player returns), 0 = not suspended. A red card sets
   *  `sentOff = true` (every "is he on the pitch" check keeps working) AND this; `resolveSuspensions`
   *  returns him at this tick. In codec + hashState + the snapshot wire (the return-countdown clock). */
  suspendedUntilTick: number;
  isGk: boolean;
  punchHit: boolean; // did the in-progress punch land? (selects PUNCH_RECOVERY hit vs whiff duration, §5.4)
  lastContact: { by: EntityId; tick: Tick; impulse: Vec2 } | null;
}

export interface BallState {
  pos: Vec3;
  vel: Vec3;
  spin: Vec3; // ω rad/s, |ω| ≤ MAX_SPIN (§5.3)
  possessor: EntityId | null;
  /** D18: keeper who last released from GK_HOLD — may not re-handle until another player touches. */
  gkLock: EntityId | null;
  claimCooldownTicks: number; // knock-free window after a kick/knock-on (§5.6 KNOCK_FREE_TICKS). See DEVIATIONS D7.
  /** D43: while claimCooldownTicks runs, ONLY this entity is blocked from claiming (null = everyone).
   *  A kicker can't instantly re-glue his own kick, but a RECEIVER controls it immediately. */
  claimCooldownOnly: EntityId | null;
  /** D43: entity whose deliberate KICK put the ball in flight (null once anyone touches it, or on
   *  knock-ons/spills). Pass-follow switching keys on THIS — a dribble touch is not a pass. */
  kickedBy: EntityId | null;
  trappedTicks: number; // consecutive slow+free ticks → DROP_BALL (§6.2). In codec + hashState. See DEVIATIONS D14.
  shotPending: boolean; // the last touch was a goal-ward shot, not yet resolved → MISS if it goes out (§6.2). See D14.
  lastTouch: { entity: EntityId; team: TeamIdx; tick: Tick } | null;
}

/** Build a PlayerState with sensible defaults (§4.3). Used by tests and initial-state build. */
export function createPlayer(
  id: EntityId,
  team: TeamIdx,
  pos: Vec2,
  over: Partial<PlayerState> = {},
): PlayerState {
  return {
    id,
    team,
    pos: { x: pos.x, y: pos.y },
    vel: { x: 0, y: 0 },
    facing: 0,
    z: 0,
    vz: 0,
    stamina: 1,
    action: 'IDLE',
    actionTicks: 0,
    charge: null,
    sprinting: false,
    prevButtons: 0,
    shouting: false,
    calling: false,
    offsideLiable: false,
    intentBits: 0,
    intentTick: -1000,
    card: null,
    sentOff: false,
    suspendedUntilTick: 0,
    isGk: false,
    punchHit: false,
    lastContact: null,
    ...over,
  };
}

export interface RefereeState {
  pos: Vec2;
  vel: Vec2;
  viewDir: number;
  aiTargetPos: Vec2;
  scanTicksLeft: number; // scan timing drawn from state.rng (§7.5)
}

export interface PerPlayerDiscipline {
  yellows: 0 | 1 | 2;
  red: boolean;
  diveReputation: number;
  foulsCommitted: number;
  foulsSuffered: number;
  punchesThrown: number;
  divesAttempted: number;
  divesWon: number;
  divesCaught: number;
}
export interface DisciplineState {
  perPlayer: PerPlayerDiscipline[];
}

export interface RestartContext {
  kind: RestartKind;
  spot: Vec2;
  team: TeamIdx; // team taking the restart
  /** D38: the designated taker — the ONLY entity whose touch/kick connects while the ball is dead
   *  (teleported to the spot by startRestart; null on placeholder contexts, e.g. mid-celebration). */
  taker: EntityId | null;
}

/** Advantage record (§7.3). Populated in M5; null otherwise. */
export interface AdvantageState {
  offender: EntityId;
  victim: EntityId;
  victimTeam: TeamIdx;
  severity: number;
  ticksLeft: number;
  restart: RestartContext;
}

/** A card awaiting brandishing at the next stoppage (§7.1/§7.6). */
export interface PendingCard {
  entity: EntityId;
  card: CardKind;
  reason: 'FOUL' | 'SIMULATION' | 'VIOLENT' | 'SECOND_YELLOW' | 'REF_ABUSE';
}

/**
 * The goal celebration in progress (D108). Lives in state — not derived per tick — because the
 * own-goal celebrant draw and the clip variant are RNG draws: redrawing them every tick would both
 * burn the normative draw order and make the ceremony flicker. Non-null only during
 * GOAL_CELEBRATION; `advancePhase` clears it on the way out.
 */
export interface CelebrationState {
  /** Whoever the goal was credited to. `null` on a genuine own goal — nobody to celebrate. */
  scorer: EntityId | null;
  /** The team doing the celebrating (i.e. the team that scored, never the own-goaller's). */
  team: TeamIdx;
  /** Who plays the CELEBRATE clip: the scorer, or a seeded few of the team on an own goal. */
  celebrants: EntityId[];
}
// No clip variant here on purpose: which celebration ANIMATION plays is presentation (§13.5), the sim
// must not know how many clips the client ships, and this record is not in the snapshot codec — so a
// variant stored here would be a field nothing could ever read. The client derives it from the GOAL
// event instead, which every client receives identically, so they all show the same celebration.

export interface MatchState {
  tick: Tick;
  rng: number; // mulberry32 u32 — ALL in-sim randomness
  phase: MatchPhase;
  celebration: CelebrationState | null; // D108 — non-null only during GOAL_CELEBRATION
  phaseTicksLeft: number;
  restart: RestartContext | null;
  half: 1 | 2 | 3; // 3 = golden-goal overtime (§6.5)
  clockTicks: number; // advances only in PLAY
  attackDir: [1 | -1, 1 | -1];
  score: [number, number];
  players: PlayerState[]; // index == EntityId
  ball: BallState;
  referee: RefereeState;
  discipline: DisciplineState;
  advantage: AdvantageState | null;
  cardQueue: PendingCard[];
  conditions: PitchCondition; // concrete (RANDOM resolved before kickoff)
  mode: ModeInfo;
}

function defaultDiscipline(n: number): DisciplineState {
  const perPlayer: PerPlayerDiscipline[] = [];
  for (let i = 0; i < n; i++) {
    perPlayer.push({
      yellows: 0,
      red: false,
      diveReputation: 1,
      foulsCommitted: 0,
      foulsSuffered: 0,
      punchesThrown: 0,
      divesAttempted: 0,
      divesWon: 0,
      divesCaught: 0,
    });
  }
  return { perPlayer };
}

/**
 * Build a fresh MatchState (§4.3/§6.1). M1 kickabout: players laid out symmetrically, ball at the
 * centre spot, phase PLAY (the KICKOFF/restart flow arrives with the rules slice, M3). Entity ids
 * follow §3: team 0 = 0..N-1, team 1 = N..2N-1; slot 0 per team = GK when N ≥ 3.
 */
export function createMatchState(mode: ModeInfo, seed: number): MatchState {
  const n = mode.teamSize;
  const players: PlayerState[] = [];
  const half = 0.5;
  for (let team = 0 as TeamIdx; team <= 1; team = (team + 1) as TeamIdx) {
    const dir = team === 0 ? -1 : 1; // team 0 starts on −x, team 1 on +x
    for (let slot = 0; slot < n; slot++) {
      const id = team * n + slot;
      // simple staggered column on each side (proper formations land with the AI slice, M4)
      const x = dir * (2 + slot * 1.5);
      const y = (slot - (n - 1) * half) * 2;
      const p = createPlayer(id, team, { x, y }, { facing: team === 0 ? 0 : Math.PI, isGk: n >= 3 && slot === 0 });
      players.push(p);
    }
  }
  return {
    tick: 0,
    rng: seed >>> 0,
    phase: 'PLAY',
    celebration: null,
    phaseTicksLeft: 0,
    restart: null,
    half: 1,
    clockTicks: 0,
    attackDir: [1, -1],
    score: [0, 0],
    players,
    ball: { pos: { x: 0, y: 0, z: 0 }, vel: { x: 0, y: 0, z: 0 }, spin: { x: 0, y: 0, z: 0 }, possessor: null, gkLock: null, claimCooldownTicks: 0, claimCooldownOnly: null, kickedBy: null, trappedTicks: 0, shotPending: false, lastTouch: null },
    referee: { pos: { x: 0, y: 6 }, vel: { x: 0, y: 0 }, viewDir: 0, aiTargetPos: { x: 0, y: 0 }, scanTicksLeft: 0 },
    discipline: defaultDiscipline(2 * n),
    advantage: null,
    cardQueue: [],
    conditions: mode.conditions,
    mode,
  };
}
